export type SupportActivityState =
  | 'idle'
  | 'typing'
  | 'recording_voice'
  | 'selecting_emoji'
  | 'selecting_sticker'
  | 'selecting_gif'
  | 'uploading_photo'
  | 'uploading_video'
  | 'uploading_file'
  | 'uploading_voice';

export type SupportPickerActivityState =
  | 'selecting_emoji'
  | 'selecting_sticker'
  | 'selecting_gif';

export type SupportRealtimeActivity = {
  type: 'activity';
  ticket_id: number;
  user_id: number;
  author_role: 'user' | 'support';
  author_telegram_id: number;
  state: SupportActivityState;
};

const ACTIVITY_TTL_MS = 5000;

export function activityLabel(state: SupportActivityState | null | undefined): string | null {
  switch (state) {
    case 'typing':
      return 'печатает…';
    case 'recording_voice':
      return 'записывает голосовое…';
    case 'selecting_emoji':
      return 'выбирает эмодзи…';
    case 'selecting_sticker':
      return 'выбирает стикер…';
    case 'selecting_gif':
      return 'выбирает GIF…';
    case 'uploading_photo':
      return 'загружает фото…';
    case 'uploading_video':
      return 'загружает видео…';
    case 'uploading_file':
      return 'загружает файл…';
    case 'uploading_voice':
      return 'отправляет голосовое…';
    default:
      return null;
  }
}

/** Prefer live activity over static ticket status when something is happening. */
export function statusOrActivityLabel(
  status: string,
  activity: SupportActivityState | null | undefined,
  statusLabelFn: (s: string) => string,
): { text: string; isActivity: boolean } {
  const live = activityLabel(activity);
  if (live && status !== 'closed') {
    return { text: live, isActivity: true };
  }
  return { text: statusLabelFn(status), isActivity: false };
}

export type MessageReceiptStatus = 'pending' | 'sent' | 'delivered' | 'read';

export function messageReceiptStatus(message: {
  id: number;
  delivered_at?: string | null;
  read_at?: string | null;
}): MessageReceiptStatus {
  if (message.id < 0) return 'pending';
  if (message.read_at) return 'read';
  if (message.delivered_at) return 'delivered';
  return 'sent';
}

export function activityStateFromFiles(
  files: Array<Blob | File>,
  opts?: { asVoice?: boolean },
): SupportActivityState {
  if (opts?.asVoice) return 'uploading_voice';
  if (!files.length) return 'typing';

  let photos = 0;
  let videos = 0;
  let other = 0;
  for (const f of files) {
    const name = f instanceof File ? f.name : '';
    const mime = ('type' in f && f.type) || '';
    if (mime.startsWith('video/') || /\.(mp4|webm|mov)$/i.test(name)) videos += 1;
    else if (mime.startsWith('image/') || /\.(jpe?g|png|gif|webp)$/i.test(name)) photos += 1;
    else other += 1;
  }
  if (other > 0) return 'uploading_file';
  if (photos > 0 && videos > 0) return 'uploading_file';
  if (videos > 0) return 'uploading_video';
  if (photos > 0) return 'uploading_photo';
  return 'uploading_file';
}

/** Keep peer activity visible briefly; auto-clear if idle/update was lost. */
export function scheduleActivityClear(
  timerRef: { current: number | null },
  clear: () => void,
  ttlMs = ACTIVITY_TTL_MS,
): void {
  if (timerRef.current != null) window.clearTimeout(timerRef.current);
  timerRef.current = window.setTimeout(() => {
    timerRef.current = null;
    clear();
  }, ttlMs);
}
